import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router";

import { getWebhookLog, getWebhooks } from "@/api/stackApi";
import { HELP_LOGS_WEBHOOKS } from "@/constants/HelpUrls";
import FabBar from "@/design/components/FabBar";
import MessageStack from "@/design/components/MessageStack";
import Page from "@/design/layouts/Page";
import { setHelpUrl } from "@/store/reducers/ui";
import useNow from "@/utils/hooks/useNow";
import Valkyrie, { viCircleXmark, viWebhooks, viWebhooksClock } from "@sippy-platform/valkyrie";

import { Box, Card, CircularProgress, Container, Divider, Pagination, Paper } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { differenceInDays, endOfDay, format, isValid, isWithinInterval, parseISO, startOfDay } from "date-fns";

import ClearFilterAction from "../_Components/ClearFilterAction";
import DateFilter from "../_Components/DateFilter";
import FilterBar from "../_Components/FilterBar";
import FilterDropdown from "../_Components/FilterDropdown";
import RefreshActions from "../_Components/RefreshActions";
import TableHead from "../_Components/TableHead";
import TableHeading from "../_Components/TableHeading";
import WebhookItem from "./WebhookItem";

export default function FlexDesKLog() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const pbx = useSelector((state) => state.domain.pbx);
  const logSettings = useSelector((state) => state.settings.logs);

  const dateFormat = "yyyy-MM-dd'T'HH:mm:ss.SSS";

  useEffect(() => {
    dispatch(setHelpUrl(HELP_LOGS_WEBHOOKS));
  }, [dispatch]);

  // Filter preparations
  const webhooksQuery = useQuery({
    queryKey: [pbx.context, "webhooks"],
    queryFn: getWebhooks,
    refetchInterval: 10 * 1000 * 60,
  });

  // Filters
  const now = useNow();
  const { page, order, orderBy, startDate, endDate, webhook } = useMemo(
    () => ({
      page: Number(searchParams.get("page") ?? 0),
      order: searchParams.get("order") === "asc" ? "asc" : "desc",
      orderBy: ["date", "name", "did", "eventType", "responseData", "url"].includes(searchParams.get("orderBy"))
        ? searchParams.get("orderBy")
        : "date",
      startDate:
        searchParams.get("startDate") && isValid(parseISO(searchParams.get("startDate")))
          ? searchParams.get("startDate")
          : format(startOfDay(now), dateFormat),
      endDate:
        searchParams.get("endDate") && isValid(parseISO(searchParams.get("endDate")))
          ? searchParams.get("endDate")
          : format(endOfDay(now), dateFormat),
      webhook: webhooksQuery.data?.find((webhook) => Number(searchParams.get("webhook")) === webhook.id)
        ? Number(searchParams.get("webhook"))
        : "",
    }),
    [now, searchParams, webhooksQuery.data],
  );

  const isFiltering = useMemo(
    () =>
      !(
        page === 0 &&
        order === "desc" &&
        orderBy === "date" &&
        startDate === format(startOfDay(now), dateFormat) &&
        endDate === format(endOfDay(now), dateFormat) &&
        webhook === ""
      ),
    [now, page, order, orderBy, startDate, endDate, webhook],
  );

  const setFilter = useCallback(
    (key, value, resetPage = true) => {
      searchParams.set(key, value);
      if (resetPage) searchParams.set("page", 0);

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  // Query
  const pauseRefresh = !isWithinInterval(now, { start: parseISO(startDate), end: parseISO(endDate) });
  const webhookLogQuery = useQuery({
    queryKey: [pbx.context, "log.webhooks", page, order, orderBy, startDate, endDate, webhook],
    queryFn: () =>
      getWebhookLog({
        page,
        order,
        orderBy,
        startDate,
        endDate,
        webhook,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: pauseRefresh ? 0 : logSettings.refreshInterval * 1000 * 60,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: pauseRefresh || logSettings.refreshInterval === 0 ? false : true,
    enabled:
      isValid(parseISO(startDate)) &&
      isValid(parseISO(endDate)) &&
      differenceInDays(parseISO(endDate), parseISO(startDate)) <= 31,
  });

  // Handlers
  const handlePage = (event, page) => {
    setFilter("page", page - 1, false);
  };

  return (
    <Page sx={{ pt: 8, position: "relative" }}>
      <title>{`Webhook log \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>

      <FilterBar>
        <ClearFilterAction disabled={isFiltering} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <DateFilter startDate={startDate} endDate={endDate} setFilter={setFilter} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <FilterDropdown
          id="webhook"
          icon={viWebhooks}
          options={webhooksQuery.data || []}
          defaultOptions={[{ name: "All webhooks", value: "" }]}
          value={webhook}
          setValue={(value) => setFilter("webhook", value)}
          getOptionLabel={(x) => x.name}
          getOptionValue={(x) => x.id ?? x.value}
          searchKeys={["name"]}
        />
        <Box sx={{ flexGrow: 1 }} />
        <RefreshActions
          onClick={() => webhookLogQuery.refetch()}
          isRefreshing={webhookLogQuery.isFetching}
          isPaused={pauseRefresh}
        />
      </FilterBar>

      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, auto)",
            gridTemplateRows: "auto auto",
            gap: 1,
          }}
        >
          <TableHead>
            <TableHeading id="date" setFilter={setFilter} order={order} orderBy={orderBy}>
              Time
            </TableHeading>
            <TableHeading id="name" setFilter={setFilter} order={order} orderBy={orderBy}>
              Webhook
            </TableHeading>
            <TableHeading id="did" setFilter={setFilter} order={order} orderBy={orderBy}>
              DID
            </TableHeading>
            <TableHeading id="eventType" setFilter={setFilter} order={order} orderBy={orderBy}>
              Event
            </TableHeading>
            <TableHeading id="responseData" setFilter={setFilter} order={order} orderBy={orderBy}>
              Response
            </TableHeading>
          </TableHead>
          {webhookLogQuery.isFetched ? (
            <>
              {webhookLogQuery.data?.wel?.length >= 1 ? (
                <Card sx={{ display: "grid", gridTemplateColumns: "subgrid", gridColumn: "1 / -1", mb: 6.5 }}>
                  {webhookLogQuery.data?.wel.map((webhookEvent, eventKey) => (
                    <WebhookItem key={eventKey} webhookEvent={webhookEvent} />
                  ))}
                </Card>
              ) : (
                <MessageStack
                  icon={<Valkyrie icon={viWebhooksClock} />}
                  message="No data for your current filters..."
                />
              )}
            </>
          ) : webhookLogQuery.isError ? (
            <MessageStack
              icon={<Valkyrie icon={viCircleXmark} />}
              color="error"
              message="We couldn't finish your request..."
              description={webhookLogQuery?.error?.message}
            />
          ) : (
            <MessageStack icon={<CircularProgress size={36} />} message="Getting your data..." />
          )}
        </Box>

        <FabBar sx={{ px: 1, justifyContent: "center" }}>
          <Paper
            variant="acrylic"
            sx={{
              whiteSpace: "nowrap",
              display: {
                xs: "none",
                md: "flex",
              },
              alignItems: "center",
              justifyContent: "center",
              px: 1.5,
              py: 0.75,
            }}
          >
            <Box>
              {page * 50 + 1}-
              {(page + 1) * 50 > webhookLogQuery.data?.count ? webhookLogQuery.data?.count : (page + 1) * 50} of{" "}
              {webhookLogQuery.data?.count}
            </Box>
          </Paper>
          {Math.ceil(webhookLogQuery.data?.count / 50) > 1 && (
            <Pagination count={Math.ceil(webhookLogQuery.data?.count / 50)} page={page + 1} onChange={handlePage} />
          )}
        </FabBar>
      </Container>
    </Page>
  );
}
